/**
 * CalendarioSection — semana laborable, plazos y festivos (MVP-02C §3.6).
 *
 * Transportista, modalidad y servicio los fija el backend a Correos /
 * domicilio / estándar (D-04): aquí no hay selector para cambiarlos, solo se
 * muestran como lectura.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Plus, RotateCw } from "lucide-react";

import { TextInput, confirmDelete, showErrorAlert } from "../../../../../components/ui/index.js";
import {
    activateWorkingWeek, createCalendarException, createWorkingWeek,
    fetchCalendarExceptions, fetchShippingConfig, fetchWorkingWeeks,
    parseAdminErrors, toggleCalendarException, updateShippingConfig,
} from "../../../../../domain/shipping-admin/api.js";

const firstErrorMessage = (parsed) =>
    parsed.detail || parsed.general || Object.values(parsed)[0] || "No se pudo completar la operación.";

const DAYS = [
    ["monday", "Lunes"], ["tuesday", "Martes"], ["wednesday", "Miércoles"],
    ["thursday", "Jueves"], ["friday", "Viernes"], ["saturday", "Sábado"], ["sunday", "Domingo"],
];

function ConfigPanel({ config, onChanged }) {
    const [value, setValue] = useState(config);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const setField = (field, v) => {
        setValue((prev) => ({ ...prev, [field]: v }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            await updateShippingConfig({
                handling_days: Number(value.handling_days),
                transit_min_days: Number(value.transit_min_days),
                transit_max_days: Number(value.transit_max_days),
            });
            setDirty(false);
            onChanged();
        } catch (err) {
            setError(parseAdminErrors(err).general || parseAdminErrors(err).transit_min_days || "No se pudo guardar.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="flex flex-col gap-3">
            <h3 className="font-semibold text-sm">Manipulación y tránsito</h3>
            <p className="text-xs text-base-content/60">
                Transportista <strong>{config.carrier}</strong>, modalidad{" "}
                <strong>{config.modality}</strong>, servicio <strong>{config.service_code}</strong>{" "}
                (fijados por el sistema; no hay selector aquí).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="form-control gap-1">
                    <label className="label py-0" htmlFor="cfg-handling">
                        <span className="label-text text-xs font-semibold">Manipulación (días laborables)</span>
                    </label>
                    <TextInput id="cfg-handling" type="number" min="0" value={value.handling_days}
                        onChange={(e) => setField("handling_days", e.target.value)} />
                </div>
                <div className="form-control gap-1">
                    <label className="label py-0" htmlFor="cfg-tmin">
                        <span className="label-text text-xs font-semibold">Tránsito mínimo</span>
                    </label>
                    <TextInput id="cfg-tmin" type="number" min="0" value={value.transit_min_days}
                        onChange={(e) => setField("transit_min_days", e.target.value)} />
                </div>
                <div className="form-control gap-1">
                    <label className="label py-0" htmlFor="cfg-tmax">
                        <span className="label-text text-xs font-semibold">Tránsito máximo</span>
                    </label>
                    <TextInput id="cfg-tmax" type="number" min="0" value={value.transit_max_days}
                        onChange={(e) => setField("transit_max_days", e.target.value)} />
                </div>
            </div>
            {error && <p role="alert" className="text-sm text-error">{error}</p>}
            <button type="button" className="btn btn-sm btn-primary self-start" disabled={!dirty || saving} onClick={save}>
                {saving ? "Guardando…" : "Guardar cambios"}
            </button>
            <p className="text-xs text-base-content/50">
                El intervalo se comunica como estimación: disponibilidad + manipulación +
                tránsito. Nunca es un plazo garantizado.
            </p>
        </section>
    );
}
ConfigPanel.propTypes = { config: PropTypes.object.isRequired, onChanged: PropTypes.func.isRequired };

function WorkingWeekPanel({ weeks, onChanged }) {
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({
        label: "", monday: true, tuesday: true, wednesday: true, thursday: true,
        friday: true, saturday: false, sunday: false,
    });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [activatingId, setActivatingId] = useState(null);

    const activate = async (week) => {
        const ok = await confirmDelete({
            title: `¿Activar esta versión de la semana laborable?`,
            text: "Sustituirá de inmediato a la semana activa: cambia qué días cuentan para nuevas estimaciones de entrega.",
            confirmButtonText: "Activar",
        });
        if (!ok) return;

        setActivatingId(week.id);
        try {
            await activateWorkingWeek(week.id);
            onChanged();
        } catch (err) {
            showErrorAlert({ text: firstErrorMessage(parseAdminErrors(err)) });
        } finally {
            setActivatingId(null);
        }
    };

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            await createWorkingWeek(draft);
            setCreating(false);
            onChanged();
        } catch (err) {
            setError(parseAdminErrors(err).general || parseAdminErrors(err).monday || "No se pudo crear.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="flex flex-col gap-3 border-t border-base-300 pt-4">
            <h3 className="font-semibold text-sm">Semana laborable</h3>
            {weeks.length === 0 ? (
                <p className="text-sm text-base-content/50">Todavía no hay ninguna versión.</p>
            ) : (
                <ul className="flex flex-col gap-1.5">
                    {weeks.map((week) => (
                        <li key={week.id} className="flex items-center gap-2 text-sm">
                            <span className={`badge badge-sm ${week.is_active ? "badge-success" : "badge-ghost"}`}>
                                {week.is_active ? "Activa" : `v${week.version}`}
                            </span>
                            <span>{week.label || `Versión ${week.version}`}</span>
                            <span className="text-xs text-base-content/50">
                                {DAYS.filter(([key]) => week[key]).map(([, label]) => label.slice(0, 1)).join(" ")}
                            </span>
                            {!week.is_active && (
                                <button type="button" className="btn btn-ghost btn-xs"
                                    disabled={activatingId === week.id}
                                    onClick={() => activate(week)}>
                                    {activatingId === week.id ? "Activando…" : "Activar"}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {creating ? (
                <form onSubmit={submit} className="rounded-lg border border-base-300 p-3 flex flex-col gap-3">
                    <TextInput value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                        placeholder="Etiqueta (opcional)" inputSize="sm" />
                    <div className="flex flex-wrap gap-3">
                        {DAYS.map(([key, label]) => (
                            <label key={key} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                <input type="checkbox" className="checkbox checkbox-sm checkbox-primary"
                                    checked={draft[key]}
                                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))} />
                                {label}
                            </label>
                        ))}
                    </div>
                    {error && <p role="alert" className="text-sm text-error">{error}</p>}
                    <div className="flex gap-2">
                        <button type="submit" className="btn btn-xs btn-primary" disabled={saving}>Guardar</button>
                        <button type="button" className="btn btn-xs btn-ghost" onClick={() => setCreating(false)}>Cancelar</button>
                    </div>
                </form>
            ) : (
                <button type="button" onClick={() => setCreating(true)} className="btn btn-outline btn-sm gap-1 self-start">
                    <Plus className="w-4 h-4" /> Nueva versión
                </button>
            )}
        </section>
    );
}
WorkingWeekPanel.propTypes = { weeks: PropTypes.array.isRequired, onChanged: PropTypes.func.isRequired };

function ExceptionsPanel({ exceptions, onChanged }) {
    const [date, setDate] = useState("");
    const [label, setLabel] = useState("");
    const [kind, setKind] = useState("festivo");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    const toggle = async (exc) => {
        const activating = !exc.is_active;
        const ok = await confirmDelete({
            title: activating ? `¿Activar «${exc.label || exc.date}»?` : `¿Desactivar «${exc.label || exc.date}»?`,
            text: activating
                ? "Ese día pasará a excluirse de nuevas estimaciones de entrega."
                : "Ese día volverá a contar como laborable para nuevas estimaciones.",
            confirmButtonText: activating ? "Activar" : "Desactivar",
        });
        if (!ok) return;

        setTogglingId(exc.id);
        try {
            await toggleCalendarException(exc.id, activating);
            onChanged();
        } catch (err) {
            showErrorAlert({ text: firstErrorMessage(parseAdminErrors(err)) });
        } finally {
            setTogglingId(null);
        }
    };

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            await createCalendarException({ date, label, kind, is_active: true });
            setDate("");
            setLabel("");
            onChanged();
        } catch (err) {
            setError(parseAdminErrors(err).general || parseAdminErrors(err).date || "No se pudo crear.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="flex flex-col gap-3 border-t border-base-300 pt-4">
            <h3 className="font-semibold text-sm">Festivos y cierres</h3>
            {exceptions.length === 0 ? (
                <p className="text-sm text-base-content/50">Sin excepciones registradas.</p>
            ) : (
                <ul className="flex flex-col gap-1.5">
                    {exceptions.map((exc) => (
                        <li key={exc.id} className="flex items-center gap-2 text-sm">
                            <span className={`badge badge-sm ${exc.is_active ? "badge-warning" : "badge-ghost"}`}>
                                {exc.kind}
                            </span>
                            <span>{exc.date}</span>
                            <span className="text-base-content/60">{exc.label}</span>
                            <button type="button" className="btn btn-ghost btn-xs ml-auto"
                                disabled={togglingId === exc.id}
                                onClick={() => toggle(exc)}>
                                {togglingId === exc.id ? "…" : exc.is_active ? "Desactivar" : "Activar"}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
                <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} inputSize="sm" aria-label="Fecha" />
                <select className="select select-bordered select-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
                    <option value="festivo">Festivo</option>
                    <option value="cierre">Cierre</option>
                </select>
                <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Motivo" inputSize="sm" />
                <button type="submit" className="btn btn-sm btn-outline" disabled={saving || !date}>Añadir</button>
            </form>
            {error && <p role="alert" className="text-sm text-error">{error}</p>}
        </section>
    );
}
ExceptionsPanel.propTypes = { exceptions: PropTypes.array.isRequired, onChanged: PropTypes.func.isRequired };

export default function CalendarioSection({ onChanged }) {
    const [state, setState] = useState({ status: "loading", config: null, weeks: [], exceptions: [] });

    const cargar = useCallback(() => {
        setState((prev) => ({ ...prev, status: "loading" }));
        return Promise.all([fetchShippingConfig(), fetchWorkingWeeks(), fetchCalendarExceptions()])
            .then(([config, weeks, exceptions]) => setState({ status: "ready", config, weeks, exceptions }))
            .catch(() => setState((prev) => ({ ...prev, status: "error" })));
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleChanged = () => {
        cargar();
        onChanged();
    };

    if (state.status === "loading") {
        return (
            <div className="flex flex-col gap-2" aria-busy="true">
                <div className="h-24 rounded-lg bg-base-300/60 animate-pulse" />
                <span className="sr-only">Cargando calendario…</span>
            </div>
        );
    }
    if (state.status === "error") {
        return (
            <div role="alert" className="flex flex-col items-start gap-3 py-6">
                <p className="text-sm text-error">No se pudo cargar el calendario.</p>
                <button type="button" className="btn btn-sm gap-1" onClick={cargar}>
                    <RotateCw className="w-4 h-4" /> Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col gap-6">
            <ConfigPanel config={state.config} onChanged={handleChanged} />
            <WorkingWeekPanel weeks={state.weeks} onChanged={handleChanged} />
            <ExceptionsPanel exceptions={state.exceptions} onChanged={handleChanged} />
        </div>
    );
}

CalendarioSection.propTypes = { onChanged: PropTypes.func.isRequired };
